import { useEffect, useState } from 'react'

import { Box, Container } from '@mui/material'
import { GAME_STEP } from 'constants/enums/GameStepEnum'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { BusinessTopicType } from 'constants/enums/TopicTypeEnum'
import { SUBMITTED_IDEAS } from 'constants/routes'
import { alertTitleTextMap, defaultErrorMessage } from 'content/alertText'
import { brainstormText } from 'content/brainstormText'
import { hasUnsavedIdeation } from 'utils/ideationUtils'
import logger from 'utils/logger'
import {
	guardUnsavedAction,
	useUnsavedIdeationWarning,
} from 'utils/useUnsavedIdeationWarning'

import { DressUpModel } from 'models/DressUpModel'
import { Game } from 'models/gameModels'

import { useIdeaStreakContext } from 'contexts/IdeaStreakProvider'
import { useModalContext } from 'contexts/ModalProvider'
import { useToastContext } from 'contexts/ToastProvider'

import { postDressUpPrompt } from 'services/completions.service'
import { postIdea } from 'services/ideas.service'

import BreadCrumbs from 'components/BreadCrumbs'
import CustomGameTitleBar from 'components/CustomGameTitleBar/CustomGameTitleBar'
import { IdeationRecommendationModal } from 'components/IdeationRecommendationModal/IdeationRecommendationModal'

import Hero from '../../components/Hero/Hero'
import { removeExtraNewLinesFromTraits } from '../../utils/stringFormat'
import Section1 from './components/Section1/Section1'
import Section2 from './components/Section2'
import Section3 from './components/Section3'

export const TEST_ID = 'dress-up'

const DressUp = ({ game }: { game?: Game }) => {
	const [refreshClicksSinceIdeation, setRefreshClicksSinceIdeation] =
		useState(0)

	useEffect(() => {
		if (game) {
			setInnovationCompany(game.innovationCompany)
			setInnovationTopic(game.innovationTopic)
			setCompanyProblem(game.companyProblem)
			setGameId(game.gameId)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [game])

	const {
		innovationCompany,
		setInnovationCompany,
		companyProblem,
		setCompanyProblem,
		authorIdeation,
		setAuthorIdeation,
		innovationTopic,
		setInnovationTopic,
		setPromptResponse,
		promptToJSON,
		resetSoft,
		submitIdeasDisabled,
		gameToJSON,
		reset,
		setGameId,
		generateScenarioDisabled,
		termsAgreed,
		setTermsAgreed,
		dressUpPrompt,
		updateCompanyAdvice,
		setPreviousComparatorCompanies,
	} = DressUpModel()

	const { showModal } = useModalContext()
	const [isSubmitButtonLoading, setIsSubmitButtonLoading] = useState(false)
	const [isPromptLoading, setIsPromptLoading] = useState(false)
	const [step, setStep] = useState<GAME_STEP>(GAME_STEP.Step1)
	const isUnsaved = hasUnsavedIdeation(step, authorIdeation)

	useUnsavedIdeationWarning(isUnsaved, authorIdeation, showModal)

	const { setIdeaStreakCount } = useIdeaStreakContext()
	const { showAlert } = useToastContext()

	const resetAllField = () => {
		setInnovationCompany('')
		setInnovationTopic('')
		setCompanyProblem('')
	}

	const refreshPrompt = async () => {
		return guardUnsavedAction({
			isUnsaved,
			showModal,
			onProceed: async () => {
				if (refreshClicksSinceIdeation < 2) {
					await createPrompt()
				}

				setRefreshClicksSinceIdeation((prev) => prev + 1)
			},
		})
	}

	const guardedUpdateCompanyAdvice = async (
		updatedTraits: string,
		companyIndex: number
	) => {
		return guardUnsavedAction({
			isUnsaved,
			showModal,
			onProceed: () => updateCompanyAdvice(updatedTraits, companyIndex),
		})
	}

	const createPrompt = async () => {
		try {
			setIsPromptLoading(true)
			resetSoft()

			// set up the conversation
			const prompt = promptToJSON()
			setStep(GAME_STEP.Step2)
			window.scrollTo(0, 0)
			const response = await postDressUpPrompt(prompt)

			// setup history
			if (response.status === 200 && response.data) {
				if (typeof response.data !== 'object') {
					throw Error(
						`invalid response type. expected: object, actual: ${typeof response.data}`
					)
				}

				setPreviousComparatorCompanies((prevCompanies) => [
					...prevCompanies,
					...response.data.companies.map((x) => x.name),
				])
				const modifiedData = removeExtraNewLinesFromTraits(
					response.data
				)
				const modifiedResponse = { ...response }
				modifiedResponse.data = modifiedData

				setPromptResponse(modifiedResponse)
			}
		} catch (error) {
			logger.error(error)
			if (showAlert) {
				showAlert({
					title: defaultErrorMessage.title,
					bodyText: defaultErrorMessage.bodyText,
					severity: ToastSeverity.ERROR,
				})
			}
		} finally {
			setIsPromptLoading(false)
		}
	}

	const viewSubmittedIdeas = () => {
		window.open(`${window.location.origin}${SUBMITTED_IDEAS}`, '_blank')
	}

	const handleSubmitIdeas = async () => {
		setIsSubmitButtonLoading(true)

		try {
			const response = await postIdea(gameToJSON())
			if (response.status !== 200) {
				throw new Error(
					`Failed to submit idea. Status: ${response.status}`
				)
			}

			showAlert?.({
				severity: ToastSeverity.SUCCESS,
			})

			setIdeaStreakCount((prevState) => prevState + 1)
			setRefreshClicksSinceIdeation(0)
			// need to reset the model
			reset()
			window.scrollTo({
				top: 0,
			})

			// Load new prompt
			await createPrompt()
		} catch (error) {
			logger.error(error)
			if (showAlert) {
				showAlert({
					title: alertTitleTextMap.submitIdeaError.title,
					bodyText: alertTitleTextMap.submitIdeaError.text,
					severity: ToastSeverity.ERROR,
				})
			}
		} finally {
			setIsSubmitButtonLoading(false)
		}
	}

	const onIdeationRecommendationBackClicked = () => {
		setStep(GAME_STEP.Step1)
		onIdeationRecommendationModalClosed()
	}

	const onIdeationRecommendationModalClosed = () => {
		setRefreshClicksSinceIdeation(0)
	}

	useEffect(() => {
		setRefreshClicksSinceIdeation(0)
	}, [authorIdeation])

	return (
		<Box>
			<IdeationRecommendationModal
				refreshClicksSinceIdeation={refreshClicksSinceIdeation}
				onBack={onIdeationRecommendationBackClicked}
				onClose={onIdeationRecommendationModalClosed}
			/>
			<Hero
				step={step}
				gameTypeEnum={GAME_TYPE.DRESS_UP}
				innovationTopic={innovationTopic as BusinessTopicType}
				innovationCompany={innovationCompany}
				companyProblem={companyProblem}
				isLoading={isPromptLoading || isSubmitButtonLoading}
				setCompanyProblem={setCompanyProblem}
				setInnovationCompany={setInnovationCompany}
				setInnovationTopic={setInnovationTopic}
				createPrompt={createPrompt}
				hideEdit={Boolean(game && (game.gameTitle || game.endTime))}
			/>
			<Container
				data-testid={`${TEST_ID}-container`}
				maxWidth="md"
				sx={{
					paddingTop: '3rem',
					paddingBottom: '3rem',
					paddingX: '3rem',
				}}
			>
				<Box paddingBottom={'2rem'}>
					<BreadCrumbs
						breadcrumbs={[
							{
								label: '1. INNOVATION TOPIC',
								active: step === GAME_STEP.Step1,
								onClick:
									step !== GAME_STEP.Step1
										? () => setStep(GAME_STEP.Step1)
										: undefined,
							},
							{
								label: '2. FACILITATION TECHNIQUE & 3. IDEATION',
								active: step !== GAME_STEP.Step1,
							},
						]}
					/>
				</Box>
				{game && (game.gameTitle || game.endTime) && (
					<CustomGameTitleBar game={game} />
				)}
				{step === GAME_STEP.Step1 && (
					<Section1
						isLoading={isPromptLoading || isSubmitButtonLoading}
						innovationCompany={innovationCompany}
						setInnovationCompany={setInnovationCompany}
						innovationTopic={innovationTopic as BusinessTopicType}
						setInnovationTopic={setInnovationTopic}
						companyProblem={companyProblem}
						setCompanyProblem={setCompanyProblem}
						generateScenarioDisabled={generateScenarioDisabled}
						resetAllField={resetAllField}
						createPrompt={createPrompt}
						editable={
							game
								? game.gameTitle === '' && game.endTime === ''
								: true
						}
					/>
				)}
				{step === GAME_STEP.Step2 && (
					<>
						<Section2
							loading={isPromptLoading}
							createPrompt={refreshPrompt}
							reloadIdeas={guardedUpdateCompanyAdvice}
							dressUpPrompt={dressUpPrompt}
						/>
						<Section3
							submitIdeasDisabled={submitIdeasDisabled}
							loading={isSubmitButtonLoading}
							submitIdeas={handleSubmitIdeas}
							authorIdeation={authorIdeation}
							setAuthorIdeation={setAuthorIdeation}
							viewSubmittedIdeas={viewSubmittedIdeas}
							cautionText={
								game?.gameTitle || game?.endTime
									? brainstormText.global.alternateWarningText
									: brainstormText.global.warningText
							}
							termsAgreed={termsAgreed}
							setTermsAgreed={setTermsAgreed}
							gameId={game?.gameId}
						/>
					</>
				)}
			</Container>
		</Box>
	)
}

export default DressUp
